import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  makeStyles,
  TextField,
} from "@material-ui/core";
import { LcmDialogProps } from "./DialogProps";
import { Controller, useForm } from "react-hook-form";
import { LcmExternalCounselRequest } from "../../../domain/LcmExternalRequest";
import { nameof } from "ts-simple-nameof";
import { fieldLabels } from "../../sections/fieldlabels";
import { Error } from "../../common/Error";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { counselCategoryLookupService } from "../../../services";
import { Select } from "@ofqual/portal-components/components/Select";
import { requestExternalCounsel } from "../../../redux/actions";
import { useDispatch } from "react-redux";

const defaultValues: Partial<LcmExternalCounselRequest> = {
  name: null,
  email: null,
  category: null,
  notes: null,
  forecast: null,
};

const useStyles = makeStyles(() => ({
  actions: {
    padding: 15,
  },
}));

export const AddCounselDialog: React.FC<LcmDialogProps> = ({
  lcmItem,
  onClose,
}) => {
  const { handleSubmit, control, errors } = useForm<LcmExternalCounselRequest>({
    defaultValues,
  });

  const classes = useStyles();
  const [counselCategoryLookups] = useLookup(counselCategoryLookupService);

  const dispatch = useDispatch();

  const onSubmit = (req: LcmExternalCounselRequest) => {
    dispatch(requestExternalCounsel(lcmItem.id, req));
    onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="request-counsel-dialog"
    >
      <DialogTitle data-test="activity-title">
        Request external counsel
      </DialogTitle>
      <Divider component="div" />
      <DialogContent style={{ minHeight: "250px" }}>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<LcmExternalCounselRequest>((x) => x.name)}
                rules={{ required: true }}
                as={
                  <TextField
                    variant="outlined"
                    label={fieldLabels.name}
                    data-test="request-counsel-name-field"
                    fullWidth
                    required
                  />
                }
              />
              {errors.name && (
                <Error data-test="recipients-validation">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<LcmExternalCounselRequest>((x) => x.email)}
                rules={{
                  required: true,
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: "Entered value does not match an email format",
                  },
                }}
                as={
                  <TextField
                    variant="outlined"
                    label={fieldLabels.email}
                    data-test="request-counsel-email-field"
                    fullWidth
                    required
                  />
                }
              />
              {errors.email?.type === "required" && (
                <Error data-test="email-missing-validation">
                  This field is required
                </Error>
              )}
              {errors.email?.type === "pattern" && (
                <Error data-test="email-format-validation">
                  {errors.email.message}
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<LcmExternalCounselRequest>(
                  (x) => x.category
                )}
                rules={{ required: true }}
                as={
                  <Select
                    variant="outlined"
                    label={fieldLabels.counselCategory}
                    data-test="request-counsel-category-field"
                    fullWidth
                    required
                  >
                    {counselCategoryLookups}
                  </Select>
                }
              />
              {errors.category?.type === "required" && (
                <Error data-test="counsel-validation">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<LcmExternalCounselRequest>((x) => x.forecast)}
                rules={{
                  pattern: {
                    value: /^\d{1,15}$|(?=^.{1,15}$)^\d+\.\d{0,2}$/,
                    message:
                      "Entered value does not match the format of currency",
                  },
                }}
                as={
                  <TextField
                    type="number"
                    variant="outlined"
                    label={fieldLabels.forecast}
                    data-test="request-counsel-forecast-field"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">£</InputAdornment>
                      ),
                    }}
                  />
                }
              />
              {errors.forecast?.type === "pattern" && (
                <Error data-test="request-counsel-forecast-validation">
                  {errors.forecast.message}
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<LcmExternalCounselRequest>((x) => x.notes)}
                render={({ onChange, value }) => (
                  <>
                    <label>{fieldLabels.note}</label>
                    <MarkdownEditor
                      onChange={onChange}
                      value={value}
                      dataTest="create-note-text-field"
                    />
                  </>
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-add-note-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          data-test="add-note-message-button"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
